// İSG uzmanı portalı — Kontrol aşaması izin detayı (api-contract §31.5). İçerik tek **departman-
// kapsamlı** uçtan gelir (`GET /permits/{uuid}/department-review?department=…`): izin + sorumlu
// belgeler + personel + AI notları (Aksiyonlar) + eğitim/araç (yalnız İSG) + departman onay
// durumları. Departman **yalnız sorumlu olduğu** belgeleri görür (Hekim yalnız Hekim-belgesi) —
// backend filtreler; FE döndürüleni gösterir. Onay/ret seçili departman adına (aksiyon çubuğu).
//
// **Viewer departmanı** kullanıcının rollerinden türetilir (`useViewerDepartments`; §31 DepartmentRole
// aynası). Tek departmandaysa otomatik; birden çok departman rolündeyse küçük bir departman seçici.
//
// S33 Slice 6 — `PermitDetailPage.tsx`'in anatomisini AYNALAR: Breadcrumb → PageHeader (başlık +
// durum/departman Tag'leri + Onayla/Reddet) → `DetailLayout` (Onay durumları + İzin bilgisi +
// Sorumlu belgeler Tile'ları solda, sekmeli panel Tile'ı sağda/altta). Alt panellerin
// (`ReviewPersonnelPanel` vb.) kendi bespoke liste anatomisine BU dilimde DOKUNULMADI (Kontrol
// aşaması salt-okunur özet ekranıdır — Table dönüşümü Scope A'nın kapsamı olan izin
// sekmeleriyle sınırlıydı; bkz. `docs/BACKLOG.md`).

import {
  Alert,
  Breadcrumb,
  Button,
  ButtonSet,
  Card,
  CardContent,
  DetailLayout,
  HazardTag,
  PageHeader,
  type PermitApprovalDepartment,
  Select,
  type SelectOption,
  Spinner,
  TabPanel,
  Tabs,
  Tag,
  type WorkPermitDto,
  currentCulture,
  departmentReviewApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { DepartmentReviewActions } from '../../components/department-review/DepartmentReviewActions';
import {
  ResponsibleDocumentsCard,
  ReviewActivitiesPanel,
  ReviewPersonnelPanel,
  ReviewTrainingsPanel,
  ReviewVehiclesPanel,
} from '../../components/department-review/DepartmentReviewPanels';
import { departmentApprovalTone } from '../../components/department-review/departmentEnums';
import { getPermitStatusPresentation } from '../../components/permits/permitEnums';
import { departmentReviewKeys } from '../../lib/departmentReviewKeys';
import { useViewerDepartments } from '../../lib/departmentRoles';
import { formatDateTime } from '../../lib/format';

type DetailTab = 'personnel' | 'trainings' | 'vehicles' | 'activities';

/** Etiket + değer satırı (bilgi bloğu). Boş değer tire ile gösterilir. */
function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm text-fg">{value && value.length > 0 ? value : '—'}</dd>
    </div>
  );
}

/** Sahalar bilgi bloğu (çoklu lokasyon) — rozet listesi. */
function SitesFact({ label, sites }: { label: string; sites: WorkPermitDto['sites'] }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="flex flex-wrap gap-1.5 text-sm text-fg">
        {sites.length === 0
          ? '—'
          : sites.map((s) => (
              <Tag key={s.uuid} color="gray">
                {s.name}
              </Tag>
            ))}
      </dd>
    </div>
  );
}

export function DepartmentReviewDetailPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const { permitUuid = '' } = useParams();
  const [tab, setTab] = useState<DetailTab>('personnel');

  const viewerDepartments = useViewerDepartments();
  const [picked, setPicked] = useState<PermitApprovalDepartment | null>(null);
  const activeDepartment = picked ?? viewerDepartments[0] ?? null;

  const departmentOptions: SelectOption[] = useMemo(
    () => viewerDepartments.map((d) => ({ value: d, label: t(`permits.department.${d}`) })),
    [viewerDepartments, t],
  );

  const query = useQuery({
    queryKey: activeDepartment
      ? departmentReviewKeys.detail(permitUuid, activeDepartment)
      : ['department-review', 'detail', permitUuid, 'none'],
    queryFn: () => departmentReviewApi.getDepartmentReviewDetail(permitUuid, activeDepartment!),
    enabled: permitUuid.length > 0 && activeDepartment !== null,
    retry: false,
  });

  const detail = query.data;
  const permit = detail?.permit;
  const viewerStatus = detail?.approvals.find((a) => a.department === activeDepartment)?.status;

  const showTrainings = (detail?.trainings.length ?? 0) > 0;
  const showVehicles = (detail?.vehicles.length ?? 0) > 0;

  const tabItems = [
    { value: 'personnel', label: t('permits.detail.section.personnel') },
    ...(showTrainings
      ? [{ value: 'trainings', label: t('permits.detail.section.trainings') }]
      : []),
    ...(showVehicles ? [{ value: 'vehicles', label: t('permits.detail.section.vehicles') }] : []),
    { value: 'activities', label: t('departmentReview.detail.section.aiNotes') },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[
          { label: t('departmentReview.title'), href: '/department-review' },
          ...(permit ? [{ label: permit.title }] : []),
        ]}
      />

      {/* Birden çok departman rolündeyse hangi departman adına inceleniyor seçilir. */}
      {viewerDepartments.length > 1 && activeDepartment ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-fg">
            {t('departmentReview.detail.reviewingAs')}
          </span>
          <div className="w-56">
            <Select
              aria-label={t('departmentReview.detail.reviewingAs')}
              options={departmentOptions}
              value={activeDepartment}
              onChange={(e) => setPicked(e.target.value as PermitApprovalDepartment)}
            />
          </div>
        </div>
      ) : null}

      {activeDepartment === null ? (
        <Alert tone="warning">{t('departmentReview.detail.noDepartment')}</Alert>
      ) : query.isPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Spinner label={t('departmentReview.detail.loading')} />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <ClipboardList className="h-10 w-10 text-danger" aria-hidden="true" />
            <p className="text-sm text-muted">{resolveApiErrorMessage(query.error, t)}</p>
            <Button asChild variant="secondary">
              <Link to="/department-review">{t('departmentReview.detail.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : detail && permit ? (
        <>
          <PageHeader
            title={permit.title}
            id={permit.permitTypeName ?? undefined}
            tags={
              <>
                {/* Sıra: durum önce, hazard sonra (S34-2 design review §1 "detail header"). */}
                <Tag color={getPermitStatusPresentation(permit.status).tagColor}>
                  {t(getPermitStatusPresentation(permit.status).labelKey)}
                </Tag>
                <Tag color="blue">{t(`permits.department.${detail.department}`)}</Tag>
                <HazardTag hazardClass={permit.hazardClass} />
              </>
            }
            actions={
              <ButtonSet>
                <DepartmentReviewActions
                  permitUuid={permit.uuid}
                  department={detail.department}
                  viewerStatus={viewerStatus}
                />
              </ButtonSet>
            }
          />

          <DetailLayout
            meta={
              <>
                <Card>
                  <CardContent className="flex flex-col gap-3">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('departmentReview.detail.approvalsTitle')}
                    </h2>
                    <ul className="flex flex-col gap-2">
                      {detail.approvals.map((d) => (
                        <li
                          key={d.uuid}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-fg">
                              {t(`permits.department.${d.department}`)}
                            </span>
                            {d.escalatedFrom ? (
                              <Tag color="gray">
                                {t('departmentReview.detail.escalatedFrom', {
                                  department: t(`permits.department.${d.escalatedFrom}`),
                                })}
                              </Tag>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {d.decidedByName ? (
                              <span className="text-xs text-muted">{d.decidedByName}</span>
                            ) : null}
                            {d.decidedAt ? (
                              <span className="text-xs text-muted">
                                {formatDateTime(d.decidedAt, culture)}
                              </span>
                            ) : null}
                            <Tag color={departmentApprovalTone(d.status)}>
                              {t(`departmentReview.status.${d.status}`)}
                            </Tag>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="flex flex-col gap-5">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('permits.detail.section.details')}
                    </h2>
                    <dl className="grid grid-cols-2 gap-4">
                      <Fact
                        label={t('permits.detail.field.contractor')}
                        value={permit.contractorName}
                      />
                      <SitesFact label={t('permits.detail.field.sites')} sites={permit.sites} />
                      <Fact
                        label={t('permits.detail.field.permitType')}
                        value={permit.permitTypeName}
                      />
                      {/* Hazard artık yalnız başlıkta (yukarıda, outline Tag) — S34-2 design
                          review §1: aynı bilginin Fact satırı + Tag ikisinde birden görünmesi
                          `PermitDetailPage.tsx`in tek-kaynak emsaliyle çelişirdi. */}
                      <Fact
                        label={t('permits.form.field.validityStart')}
                        value={formatDateTime(permit.validityStart, culture)}
                      />
                      <Fact
                        label={t('permits.form.field.validityEnd')}
                        value={formatDateTime(permit.validityEnd, culture)}
                      />
                    </dl>
                    {permit.description ? (
                      <>
                        <hr className="border-border" />
                        <Fact
                          label={t('permits.detail.field.description')}
                          value={permit.description}
                        />
                      </>
                    ) : null}
                  </CardContent>
                </Card>

                <ResponsibleDocumentsCard documents={detail.responsibleDocuments} />
              </>
            }
            content={
              <div className="flex min-w-0 flex-col">
                <Tabs
                  items={tabItems}
                  value={tab}
                  onValueChange={(value) => setTab(value as DetailTab)}
                  ariaLabel={t('departmentReview.title')}
                  idPrefix="dept-review-detail"
                />
                <Card>
                  <CardContent>
                    <TabPanel value={tab} activeValue={tab} idPrefix="dept-review-detail">
                      {tab === 'trainings' && showTrainings ? (
                        <ReviewTrainingsPanel trainings={detail.trainings} />
                      ) : tab === 'vehicles' && showVehicles ? (
                        <ReviewVehiclesPanel vehicles={detail.vehicles} />
                      ) : tab === 'activities' ? (
                        <ReviewActivitiesPanel activities={detail.activities} />
                      ) : (
                        <ReviewPersonnelPanel personnel={detail.personnel} />
                      )}
                    </TabPanel>
                  </CardContent>
                </Card>
              </div>
            }
          />
        </>
      ) : null}
    </div>
  );
}
